import { GachaID, GachaTag } from 'src/replicant/gacha/gacha.ruleset';
import { Currency, Gacha } from 'src/replicant/data/Gachas.schema';
import {
  getAvailableGachas,
  getCurrentGachaEvent,
} from 'src/replicant/gacha/gacha.getters';
import StateObserver, { now, user } from 'src/StateObserver';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import * as Promo from 'src/replicant/promo';
import { isDecorationGachaEventAvailable } from '../decoration/helpers';
import { gachaGetCurrentTheme } from './gacha.theme';
import { withLoading } from 'src/lib/utils';

export async function openGachaSelectionPopup() {
  const availableGachas = getAvailableGachas(user(), now())
    .sort((a, b) => a.price - b.price) // Sort by price ascending.
    .reduce((acc, gacha) => {
      // If there's a cheaper version of the gacha, skip the more expensive one.
      if (acc.some((x) => x.num_spins === gacha.num_spins)) {
        return acc;
      }
      return [...acc, gacha];
    }, [] as Gacha[]);

  await openPopupPromise('popupGachaSelect', {
    gachas: availableGachas,
    theme: gachaGetCurrentTheme(),
  });
}

export async function purchaseGacha(gacha: PurchaseGachaArgs) {
  let purchaseAnother = false;
  do {
    let currentGacha: PurchaseGachaArgs | Gacha | undefined = gacha;
    // If user can no longer purchase discounted gacha, switch to regular one.
    if (currentGacha?.tags?.includes('discount')) {
      let gachas = getAvailableGachas(user(), now());
      // Filter out gachas with a different number of spins.
      if (currentGacha) {
        gachas = gachas.filter((x) => x.num_spins === currentGacha?.num_spins);
      }
      if (!gachas.some((x) => x.tags?.includes('discount'))) {
        currentGacha = gachas.find(
          (x) => x.num_spins === currentGacha?.num_spins,
        );
      }
    }
    if (!currentGacha) return;
    const confirm = await openPopupPromise('popupConfirmGacha', {
      gacha: currentGacha,
    });
    if (!confirm) return;

    // Block further interaction while Replicant processes the purchase.
    const items = await withLoading(() =>
      StateObserver.invoke.purchaseGacha({
        gachaId: currentGacha.id,
      }),
    );
    await openPopupPromise('popupGacha', { rewards: items, baseSkin: 'gold' });

    purchaseAnother = await openPopupPromise('popupGachaResults', {
      results: items,
    });
  } while (purchaseAnother);
}

export type PurchaseGachaArgs = {
  id: GachaID;
  num_spins: number;
  ref_price?: number;
  price: number;
  currency: Currency;
  tags?: GachaTag[];
};

function getCurrentPromo() {
  const currentEvent = getCurrentGachaEvent(user(), now());
  if (!currentEvent?.metadata?.promo) return null;
  return currentEvent.metadata.promo;
}

export function shouldShowGachaPromo() {
  // Return false if there is no current promo
  const promo = getCurrentPromo();
  if (!promo) return false;

  return (
    Promo.getters.isPromoReady(user(), promo, now()) &&
    isDecorationGachaEventAvailable()
  );
}

export async function showGachaPromo() {
  // Bail if should not show promo.
  if (!shouldShowGachaPromo()) return;

  // Bail if there is no promo.
  const promo = getCurrentPromo();
  if (!promo) return;

  await StateObserver.invoke.promoWasShown(promo);
  await openPopupPromise('popupGachaIntro', {});
}
